import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { extractOAuthParams } from '@/utils/oauth';
import { popReturnUrl } from '@low6-pickem/frontend-shared';

/**
 * OAuth Callback Component
 *
 * Handles the OAuth redirect after user authorization.
 * Extracts authorization code from URL, exchanges it for a token,
 * and redirects the user to the main application.
 *
 * This component is mounted at /auth/callback
 */
export function OAuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginWithOAuthCode } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract OAuth parameters from URL
        const params = extractOAuthParams();

        // Check for OAuth errors
        if (params.error) {
          setError(params.error_description || params.error);
          setIsProcessing(false);
          return;
        }

        // Validate token (backend already processed OAuth and returned token)
        if (!params.token) {
          setError(t('auth.callback.missingToken'));
          setIsProcessing(false);
          return;
        }

        // Extract user data from URL params
        const user = {
          sso_user_id: params.sso_user_id || '',
          username: params.username || '',
        };

        // Update auth context with token and user data
        if (loginWithOAuthCode) {
          await loginWithOAuthCode(params.token, user);
        }

        // Redirect to original deep-link URL (if any) or home
        navigate(popReturnUrl() ?? '/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.callback.failed'));
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [navigate, loginWithOAuthCode]);

  if (isProcessing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-success border-r-transparent"></div>
          <h2 className="text-xl font-semibold text-white">{t('auth.callback.completing')}</h2>
          <p className="mt-2 text-sm text-gray-400">{t('auth.callback.pleaseWait')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="max-w-md rounded-lg bg-red-900/20 border border-red-500 p-6 text-center">
          <div className="mb-4 text-red-500">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-white">{t('auth.callback.failed')}</h2>
          <p className="mb-4 text-sm text-gray-300">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="rounded-md bg-success px-4 py-2 text-sm font-medium text-white hover:bg-success-300 transition-colors"
          >
            {t('auth.callback.returnHome')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
